import React, { useEffect, useRef, useState } from "react";
import { X } from "lucide-react";
import dandarLogo from "../../assets/images/dandar-logo.png";
import transparentBg from "../../assets/images/transparent-bg.png";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import toast from "react-hot-toast";
import PopupBlockerModal from "./PopUpErrorModal";

interface PrescriptionTemplateModalProps {
  showPreview: boolean;
  onClose: () => void;
  doctorDetails: {
    name: string;
    education: string[];
    specialization: string[];
    mobileNumber: string;
    signatureImage: string;
    opRate: string;
    state: string;
    address: string;
    city: string;
    pincode: string;
    registrationNumber?: string;
    hospitalName?: string;
  };
  patientDetails: any;
  appointment: any;
  vitals: any;
  patientDiagnostics: any;
  patientMedications: any;
  patientAppointmentHistory: any;
}

const PrescriptionTemplateModal: React.FC<PrescriptionTemplateModalProps> = ({
  showPreview,
  onClose,
  doctorDetails,
  patientDetails,
  appointment,
  vitals,
  patientDiagnostics,
  patientMedications,
  patientAppointmentHistory,
}) => {
  if (!showPreview) return null;

  function extractDate(timestamp: string | number | Date) {
    const date = new Date(timestamp);
    const dateString = date.toISOString().split("T")[0];
    const options = { year: "numeric", month: "short", day: "numeric" };
    return new Date(dateString).toLocaleDateString("en-US", options);
  }

  const [isLoading, setIsLoading] = useState(false);
  const [isPopupBlockerModalOpen, setIsPopupBlockerModalOpen] = useState(false);

  const [contentHeight, setContentHeight] = useState<number | null>(null);

  // Add useEffect to measure the content height
  useEffect(() => {
    if (contentRef.current) {
      const updateHeight = () => {
        if (contentRef.current) {
          setContentHeight(contentRef.current.scrollHeight);
        }
      };

      // Initial measurement
      updateHeight();

      // Set up a ResizeObserver to detect content height changes
      const resizeObserver = new ResizeObserver(updateHeight);
      resizeObserver.observe(contentRef.current);

      // Clean up observer on unmount
      return () => {
        if (contentRef.current) {
          resizeObserver.unobserve(contentRef.current);
        }
        resizeObserver.disconnect();
      };
    }
  }, [
    showPreview,
    patientDetails,
    appointment,
    vitals,
    patientDiagnostics,
    patientMedications,
  ]);

  // Create refs for each logical section of the prescription
  const headerRef = useRef<HTMLDivElement>(null);
  const patientInfoRef = useRef<HTMLDivElement>(null);
  const vitalsRef = useRef<HTMLDivElement>(null);
  const diagnosisRef = useRef<HTMLDivElement>(null);
  const medicationRef = useRef<HTMLDivElement>(null);
  const adviceRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);

  // A4 dimensions in mm
  const A4_WIDTH = 210;
  const A4_HEIGHT = 297;
  // PDF margin in mm
  const MARGIN = 10;
  // Available content height
  const AVAILABLE_HEIGHT = A4_HEIGHT - 2 * MARGIN;

  const handleDownloadPdf = async (e: React.MouseEvent) => {
    e.preventDefault();
    if (!contentRef.current) return;

    const closeButton = document.getElementById("CloseButton");
    const downloadButton = document.getElementById("download-button");

    // Hide buttons during PDF generation
    if (closeButton) closeButton.style.display = "none";
    if (downloadButton) downloadButton.style.display = "none";

    try {
      // Wait for all images to load
      setIsLoading(true);
      const images = contentRef.current.querySelectorAll("img");
      await Promise.all(
        Array.from(images).map((img) => {
          if (img.complete) return Promise.resolve();
          return new Promise<void>((resolve, reject) => {
            img.onload = () => resolve();
            img.onerror = () => reject(new Error("Failed to load image"));
          });
        })
      );

      // Ensure fonts are loaded
      await document.fonts.ready;

      // Initialize PDF with A4 dimensions
      const pdf = new jsPDF({
        orientation: "portrait",
        unit: "mm",
        format: "a4",
        compress: true,
      });

      // Function to create and add section to PDF
      const addSectionToPdf = async (
        sectionRef: React.RefObject<HTMLDivElement>,
        pdf: jsPDF,
        yPosition: number,
        createNewPage: boolean = false
      ): Promise<number> => {
        if (!sectionRef.current) return yPosition;

        if (createNewPage) {
          pdf.addPage();
          yPosition = MARGIN;
        }

        const canvas = await html2canvas(sectionRef.current, {
          scale: 3,
          useCORS: true,
          logging: false,
          imageTimeout: 0,
          backgroundColor: "#FFFFFF",
          removeContainer: true,
        });

        const imgData = canvas.toDataURL("image/png", 0.8);
        const imgWidth = A4_WIDTH - 2 * MARGIN;
        const imgHeight = (canvas.height * imgWidth) / canvas.width;

        pdf.addImage(
          imgData,
          "PNG",
          MARGIN,
          yPosition,
          imgWidth,
          imgHeight,
          undefined,
          "FAST"
        );

        // Clean up canvas to free memory
        canvas.width = 0;
        canvas.height = 0;

        return yPosition + imgHeight;
      };

      // First, measure each section to determine how to split content
      const measureSection = async (
        sectionRef: React.RefObject<HTMLDivElement>
      ): Promise<number> => {
        if (!sectionRef.current) return 0;

        const canvas = await html2canvas(sectionRef.current, {
          scale: 1, // Lower scale for measurement only
          useCORS: true,
          logging: false,
        });

        const scaledWidth = A4_WIDTH - 2 * MARGIN;
        const scaledHeight = (canvas.height * scaledWidth) / canvas.width;

        return scaledHeight;
      };

      // Measure all sections
      const headerHeight = await measureSection(headerRef);
      const patientInfoHeight = await measureSection(patientInfoRef);
      const vitalsHeight = await measureSection(vitalsRef);
      const diagnosisHeight = await measureSection(diagnosisRef);
      const medicationHeight = await measureSection(medicationRef);
      const adviceHeight = await measureSection(adviceRef);

      // Calculate total content height
      const totalContentHeight =
        headerHeight +
        patientInfoHeight +
        vitalsHeight +
        diagnosisHeight +
        medicationHeight +
        adviceHeight;

      let currentY = MARGIN;
      let pageNum = 1;

      // Add header to first page
      currentY = await addSectionToPdf(headerRef, pdf, currentY);

      // Determine if we need pagination or if everything fits on one page
      if (totalContentHeight <= AVAILABLE_HEIGHT) {
        // Everything fits on one page - add all sections sequentially
        currentY = await addSectionToPdf(patientInfoRef, pdf, currentY);
        currentY = await addSectionToPdf(vitalsRef, pdf, currentY);
        currentY = await addSectionToPdf(diagnosisRef, pdf, currentY);
        currentY = await addSectionToPdf(medicationRef, pdf, currentY);
        currentY = await addSectionToPdf(adviceRef, pdf, currentY);
      } else {
        // Smart pagination - check if each section fits on current page

        // Add patient info
        if (currentY + patientInfoHeight <= AVAILABLE_HEIGHT) {
          currentY = await addSectionToPdf(patientInfoRef, pdf, currentY);
        } else {
          currentY = await addSectionToPdf(patientInfoRef, pdf, MARGIN, true);
          pageNum++;
        }

        // Add vitals
        if (currentY + vitalsHeight <= AVAILABLE_HEIGHT) {
          currentY = await addSectionToPdf(vitalsRef, pdf, currentY);
        } else {
          currentY = await addSectionToPdf(vitalsRef, pdf, MARGIN, true);
          pageNum++;
        }

        // Add diagnosis
        if (currentY + diagnosisHeight <= AVAILABLE_HEIGHT) {
          currentY = await addSectionToPdf(diagnosisRef, pdf, currentY);
        } else {
          currentY = await addSectionToPdf(diagnosisRef, pdf, MARGIN, true);
          pageNum++;
        }

        // Add medication - keep this section together
        if (currentY + medicationHeight <= AVAILABLE_HEIGHT) {
          currentY = await addSectionToPdf(medicationRef, pdf, currentY);
        } else {
          currentY = await addSectionToPdf(medicationRef, pdf, MARGIN, true);
          pageNum++;
        }

        // Add advice - always keep together as specified in requirements
        if (currentY + adviceHeight <= AVAILABLE_HEIGHT) {
          currentY = await addSectionToPdf(adviceRef, pdf, currentY);
        } else {
          currentY = await addSectionToPdf(adviceRef, pdf, MARGIN, true);
          pageNum++;
        }
      }

      // Generate filename
      const fileName = `${
        patientDetails ? patientDetails.name : "default"
      }-prescription.pdf`;

      // Generate a Blob from the PDF
      const pdfBlob = pdf.output("blob");

      // Create a Blob URL
      const pdfUrl = URL.createObjectURL(pdfBlob);

      // Open the PDF in a new tab
      const newWindow = window.open(pdfUrl, "_blank");
      if (!newWindow) {
        setIsPopupBlockerModalOpen(true);
        throw new Error(
          "Popup blocked by the browser. Please allow popups for this site."
        );
      }

      // Create download link
      const downloadLink = document.createElement("a");
      downloadLink.href = pdfUrl;
      downloadLink.download = fileName;
      document.body.appendChild(downloadLink);
      downloadLink.click();
      document.body.removeChild(downloadLink);

      // Clean up memory
      setTimeout(() => {
        URL.revokeObjectURL(pdfUrl);
      }, 3000);
    } catch (error) {
      console.error("Error generating PDF:", error);
      if (error instanceof Error) {
        if (error.message.includes("Popup blocked")) {
          setIsPopupBlockerModalOpen(true);
        } else {
          toast.error(error.message);
        }
      }
    } finally {
      // Always show buttons again
      if (closeButton) {
        closeButton.style.display = "block";
      }
      if (downloadButton) {
        downloadButton.style.display = "block";
      }
      setIsLoading(false);
    }
  };

  // Toasting for loading state
  useEffect(() => {
    if (isLoading && !isPopupBlockerModalOpen) {
      toast.loading("Generating PDF...", {
        id: "loading",
        duration: 10000,
      });
    } else {
      // If loading is done or popup blocker modal is shown, dismiss the loading toast
      toast.dismiss("loading");
    }
  }, [isLoading, isPopupBlockerModalOpen]);

  return (
    <>
      <section
        className="absolute inset-0 z-50"
        style={{
          height: contentHeight ? `${contentHeight + 100}px` : "auto",
          minHeight: "100vh",
        }}
      >
        <div
          className="absolute inset-0 bg-cover bg-center flex justify-center px-2 pb-2"
          style={{
            backgroundImage: `url(${transparentBg})`,
            height: contentHeight ? `${contentHeight + 100}px` : "auto",
            minHeight: "100vh",
          }}
        >
          {/* Modal for Prescription Template Preview */}
          {showPreview && (
            <>
              <div className="absolute inset-0 bg-cover bg-center flex justify-center px-2 pb-2 h-max z-50">
                <div
                  ref={contentRef}
                  id="prescription-content"
                  className="bg-white rounded-lg w-[850px] shadow-xl mt-5 p-6"
                >
                  <div className="flex flex-col items-center">
                    {/* Header section with logo and doctor info */}
                    <div ref={headerRef} className="w-full">
                      <button
                        onClick={onClose}
                        className="text-gray-400 hover:text-gray-500 float-right"
                      >
                        <X className="w-5 h-5" id="CloseButton" />
                      </button>
                      <div className="flex gap-10 pt-5 pb-3">
                        <img
                          src={dandarLogo}
                          alt="network-error"
                          className="h-16 w-25 mb-2"
                        />
                        <div className="w-full flex justify-between mt-2">
                          <div className="">
                            <span className="text-base font-medium">
                              DR. {doctorDetails.name}
                            </span>
                            <div className="pt-1">
                              <p className="font-medium">
                                {doctorDetails.education}
                              </p>
                              <p>{doctorDetails.specialization}</p>
                              <p>Reg.No. {doctorDetails.registrationNumber}</p>
                            </div>
                          </div>
                          <div
                            className="flex flex-col text-xs gap-2 font-medium"
                            style={{ color: "#929292" }}
                          >
                            <div>
                              <p>
                                {doctorDetails.hospitalName} ,{" "}
                                {doctorDetails.address} , {doctorDetails.state}{" "}
                                , {doctorDetails.city}
                              </p>
                              <p>{doctorDetails.pincode}</p>
                            </div>
                            <div
                              className="flex text-xs gap-2"
                              style={{ color: "#929292" }}
                            >
                              <span>{doctorDetails.mobileNumber}</span>
                            </div>
                          </div>
                        </div>
                      </div>
                      <hr className="mt-2 mb-6" />
                    </div>

                    {/* Patient Information Section */}
                    <div ref={patientInfoRef} className="w-full">
                      <div
                        className="flex justify-between"
                        style={{ color: "#8f4570", fontWeight: "500" }}
                      >
                        <div>
                          <div className="text-gray-800 pb-4">
                            <div className="pb-2 text-[#8f4570] text-[1.1em] font-semibold">
                              Patient
                            </div>
                            <div className="font-semibold">
                              Patient:{" "}
                              <span className="font-normal text-gray-600">
                                {patientDetails ? patientDetails.dhpId : ""}
                              </span>
                            </div>
                            <div className="font-semibold">
                              {patientDetails ? patientDetails.name : ""},
                              <span className="pl-1">
                                {patientDetails ? patientDetails.gender : ""}{" "}
                                {patientDetails ? patientDetails.age : ""}
                              </span>
                            </div>
                            <div className="font-semibold">
                              Mobile:{" "}
                              <span className="font-normal text-gray-600">
                                {patientDetails
                                  ? patientDetails.mobileNumber
                                  : ""}
                              </span>
                            </div>
                          </div>
                          <div className="pb-2 mt-2 flex flex-col gap-2">
                            <div className="text-[#8f4570] text-[1.1em] font-semibold">
                              Cheif Complaints:
                            </div>
                            <div className="font-medium text-gray-700">
                              <ul className="list-none space-y-2">
                                {appointment?.chief_complaints ? (
                                  appointment.chief_complaints
                                    .split(",")
                                    .map(
                                      (complaints: string, index: number) => (
                                        <li
                                          key={index}
                                          className="flex items-start"
                                        >
                                          <span
                                            style={{
                                              color: "#29a359",
                                              marginRight: "8px",
                                            }}
                                          >
                                            •
                                          </span>
                                          <span>{complaints.trim()}</span>
                                        </li>
                                      )
                                    )
                                ) : (
                                  <li className="flex items-start">
                                    <span
                                      style={{
                                        color: "#29a359",
                                        marginRight: "8px",
                                      }}
                                    >
                                      •
                                    </span>
                                    <span>N/A</span>
                                  </li>
                                )}
                              </ul>
                            </div>
                            <div className="text-[#8f4570] text-[1.1em] font-semibold">
                              Diagnosed Disease:
                            </div>
                            <div className="font-medium text-gray-700">
                              <ul className="list-none space-y-2">
                                {appointment?.diagnosed_disease ? (
                                  appointment.diagnosed_disease
                                    .split(",")
                                    .map((disease: string, index: number) => (
                                      <li
                                        key={index}
                                        className="flex items-start"
                                      >
                                        <span
                                          style={{
                                            color: "#29a359",
                                            marginRight: "8px",
                                          }}
                                        >
                                          •
                                        </span>
                                        <span>{disease.trim()}</span>
                                      </li>
                                    ))
                                ) : (
                                  <li className="flex items-start">
                                    <span
                                      style={{
                                        color: "#29a359",
                                        marginRight: "8px",
                                      }}
                                    >
                                      •
                                    </span>
                                    <span>N/A</span>
                                  </li>
                                )}
                              </ul>
                            </div>
                          </div>
                        </div>

                        <div
                          className={patientDetails ? `mr-4` : `mr-14`}
                          style={{ color: "black", fontWeight: "500" }}
                        >
                          <div className="pb-2">
                            Prescription Id:{" "}
                            <span className="font-normal text-gray-700">
                              {appointment ? appointment.dhr_id : ""}
                            </span>
                          </div>
                          <div className="pb-2">
                            Date:{" "}
                            <span className="font-normal text-gray-700">
                              {appointment
                                ? extractDate(appointment.appointment_date)
                                : ""}
                            </span>
                          </div>
                          <div className="pb-2">
                            Time:{" "}
                            <span className="font-normal text-gray-700">
                              {patientAppointmentHistory
                                ? patientAppointmentHistory.startTime
                                : ""}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Vitals Section */}
                    <div ref={vitalsRef} className="w-full">
                      <div className="pb-4 mt-2">
                        <div className="flex items-center space-x-2 ">
                          <span className="font-semibold text-[#8f4570] text-[1.1em]">
                            Vitals
                          </span>
                          <p style={{ color: "#929292", fontWeight: "400" }}>
                            (as declared by patient)
                          </p>
                        </div>

                        {/* Vitals Grid */}
                        <div className="grid grid-cols-3 gap-1 mt-4 text-gray-700">
                          <div className="font-semibold">
                            Weight:{" "}
                            <span className="font-normal">
                              {vitals ? vitals.weight ?? "" : ""}
                            </span>
                          </div>
                          <div className="font-semibold ml-4">
                            Temperature:{" "}
                            <span className="font-normal">
                              {vitals ? vitals.temperature ?? "" : ""}
                            </span>
                          </div>
                          <div className="font-semibold">
                            Pulse Rate:{" "}
                            <span className="font-normal">
                              {vitals ? vitals.pulseRate ?? "" : ""}
                            </span>
                          </div>
                          <div className="font-semibold">
                            Heart Rate:{" "}
                            <span className="font-normal">
                              {vitals ? vitals.heartRate ?? "" : ""}
                            </span>
                          </div>
                          <div className="font-semibold  ml-4">
                            SPO2 Level (%):{" "}
                            <span className="font-normal">
                              {vitals ? vitals.spo2 ?? "" : ""}
                            </span>
                          </div>
                          <div className="font-semibold">
                            Glucose Level (mg/dL):{" "}
                            <span className="font-normal">
                              {vitals ? vitals.glucose ?? "" : ""}
                            </span>
                          </div>
                          <div className="font-semibold">
                            Respiratory Rate(breaths/min):{" "}
                            <span className="font-normal">
                              {vitals ? vitals.respiratoryRate ?? "" : ""}
                            </span>
                          </div>
                          <div className="font-semibold  ml-4">
                            Food:{" "}
                            <span className="font-normal">
                              {vitals
                                ? vitals.vegetarian
                                  ? "Vegetarian"
                                  : "Non-Vegetarian"
                                : ""}
                            </span>
                          </div>
                          <div>
                            <span className="font-semibold">
                              {" "}
                              Blood Pressure:
                            </span>
                            <span>
                              Min:{vitals ? vitals.bloodPressureMin ?? "" : ""}{" "}
                            </span>{" "}
                            <span className="font-bold">:</span>
                            &nbsp;
                            <span className="font-normal">
                              Max :{" "}
                              {vitals ? vitals.bloodPressureMax ?? "" : ""}
                            </span>
                          </div>
                          {vitals?.pregnancyMonths && (
                            <div className="font-semibold ">
                              Pregnancy Month (month):{" "}
                              <span className="font-normal">
                                {vitals.pregnancyMonths}
                              </span>
                            </div>
                          )}
                          {vitals?.fetalHeartRate && (
                            <div className="font-semibold ">
                              Fetal Heart Rate (bpm):{" "}
                              <span className="font-normal">
                                {vitals.fetalHeartRate}
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Diagnosis Section */}
                    <div ref={diagnosisRef} className="w-full">
                      <div className="pb-4 pt-4 text-[1.1em] font-semibold text-[#8f4570]">
                        Diagnosis/ Provisional Diagnosis
                      </div>
                      <div>
                        <table className="table-auto border-collapse w-full text-left">
                          <thead>
                            <tr className="border-t-2 border-b-2 border-dashed border-gray-400">
                              <th className="w-3/10 px-4 py-2">
                                Diagnosis Name
                              </th>
                              <th className="w-7/10 px-4 py-2 text-center">
                                Description
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {patientDiagnostics &&
                            patientDiagnostics.length > 0 ? (
                              patientDiagnostics.map(
                                (diagnostic: any, index: number) => (
                                  <tr key={index} className="hover:bg-gray-50">
                                    <td className="w-3/10 px-4 py-2">
                                      {diagnostic.diagnostic_name}
                                    </td>
                                    <td className="w-7/10 px-4 py-2 text-center">
                                      {diagnostic.comments
                                        ? diagnostic.comments
                                        : "-"}
                                    </td>
                                  </tr>
                                )
                              )
                            ) : (
                              <tr>
                                <td
                                  colSpan={2}
                                  className="text-gray-500 text-sm px-4 py-2"
                                >
                                  No data available
                                </td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                      </div>
                    </div>

                    {/* Medications Section */}
                    <div ref={medicationRef} className="w-full">
                      <div className="pb-4 pt-4 text-[1.1em] font-semibold text-[#8f4570]">
                        Medication Prescribed
                      </div>

                      <div>
                        <table className="table-auto border-collapse w-full text-left">
                          <thead>
                            <tr className="border-t-2 border-b-2 border-dashed border-gray-400">
                              <th className="px-4 py-2 text-left">
                                Medicine Name
                              </th>
                              <th className="px-4 py-2 text-left">Dosage</th>
                              <th className="px-4 py-2 text-left">
                                Medicine Details
                              </th>
                              <th className="px-4 py-2 text-left">Duration</th>
                            </tr>
                          </thead>
                          <tbody>
                            {patientMedications &&
                            patientMedications.length > 0 ? (
                              patientMedications.map(
                                (med: any, index: number) => {
                                  const totalDosage =
                                    (med.dosage_morning || 0) +
                                    (med.dosage_noon || 0) +
                                    (med.dosage_night || 0);

                                  const dosageText =
                                    totalDosage === 1
                                      ? "Once"
                                      : totalDosage === 2
                                      ? "Twice"
                                      : totalDosage === 3
                                      ? "Thrice"
                                      : `${totalDosage} times`;

                                  return (
                                    <tr
                                      key={index}
                                      className="hover:bg-gray-50"
                                    >
                                      <td className="px-4 py-2 text-left font-medium ">
                                        {med.medicine_name}
                                        <p className="text-gray-500 text-xs font-normal">
                                          Contains: {med.medicine_salt}
                                        </p>
                                      </td>

                                      <td className="px-4 py-2 text-left">
                                        {med.frequency_morning
                                          ? `M:${med.dosage_morning} `
                                          : "M:0 "}
                                        {med.frequency_noon
                                          ? `N:${med.dosage_noon} `
                                          : "N:0 "}
                                        {med.frequency_night
                                          ? `N:${med.dosage_night}`
                                          : "N:0"}
                                      </td>
                                      <td className="px-4 py-2 text-left font-medium">
                                        {med.medicine_name.includes("ml") ||
                                        med.medicine_name.includes("SYP")
                                          ? "SYRUP"
                                          : med.medicine_name.includes(
                                              "Ointment"
                                            )
                                          ? "Ointment"
                                          : "TABLET"}{" "}
                                        | {dosageText} a day
                                        <p className="text-sm font-normal">
                                          Orally.{" "}
                                          {med.after_food
                                            ? "After Food"
                                            : "Before Food"}
                                        </p>
                                      </td>
                                      <td className="px-4 py-2 text-left">
                                        {med.duration_days}
                                      </td>
                                    </tr>
                                  );
                                }
                              )
                            ) : (
                              <tr>
                                <td
                                  colSpan={4}
                                  className="px-4 py-2 text-gray-500 text-sm text-center"
                                >
                                  No data available
                                </td>
                              </tr>
                            )}
                          </tbody>
                        </table>

                        <div className="flex text-sm mt-4 justify-between">
                          <div className="border border-solid border-black p-1 rounded-lg">
                            <span style={{ color: "black", fontWeight: "700" }}>
                              M-N-N:
                            </span>
                            <span>Morning -Noon- Night</span>
                          </div>

                          <div className="flex items-center border border-solid border-black p-1 rounded-lg">
                            <span>Instruction</span>
                          </div>

                          <div className="border border-solid border-black p-1 rounded-lg">
                            <span style={{ color: "black", fontWeight: "700" }}>
                              Note:
                            </span>
                            <span>
                              Medical substitution allowed wherever applicable
                            </span>
                          </div>
                        </div>

                        <div className="border-t-2 border-b-2 border-dashed border-gray-400 mt-4"></div>
                      </div>
                    </div>

                    {/* Advice and Follow-up Section - We want to keep this together */}
                    <div ref={adviceRef} className="w-full">
                      <div className="mt-3" style={{ color: "#29a359" }}>
                        {" "}
                        Advice and Instructions
                      </div>

                      <div className="flex justify-between mt-5 mb-7">
                        <ul className="list-none space-y-2">
                          {appointment?.special_instructions ? (
                            appointment.special_instructions
                              .split(",")
                              .map((instruction: string, index: number) => (
                                <li key={index} className="flex items-start">
                                  <span
                                    style={{
                                      color: "#29a359",
                                      marginRight: "8px",
                                    }}
                                  >
                                    •
                                  </span>
                                  <span>{instruction.trim()}</span>
                                </li>
                              ))
                          ) : (
                            <li className="flex items-start">
                              <span
                                style={{
                                  color: "#29a359",
                                  marginRight: "8px",
                                }}
                              >
                                •
                              </span>
                              <span>N/A</span>
                            </li>
                          )}
                          <div className="pb-2">
                            Follow-up Date:{" "}
                            <span className="font-normal text-gray-700">
                              {appointment
                                ? appointment.follow_up_date
                                  ? appointment.follow_up_date.split("T")[0]
                                  : "N/A"
                                : ""}
                            </span>
                          </div>
                        </ul>
                      </div>
                    </div>

                    <button
                      onClick={handleDownloadPdf}
                      id="download-button"
                      className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors self-end font-medium"
                    >
                      Download PDF
                    </button>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </section>

      {
        <PopupBlockerModal
          isOpen={isPopupBlockerModalOpen}
          onClose={() => setIsPopupBlockerModalOpen(false)}
        />
      }
    </>
  );
};

export default PrescriptionTemplateModal;
